import React, { useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { isEmpty } from 'lodash'
import { IconTool } from '@tabler/icons-react'

import {
  fetchToolVersions,
  resetTool,
  revertTool,
  saveTool,
} from '@/reducers/voiceAgents/voiceAgents.actions'
import { getSelectedAgent } from '@/reducers/voiceAgents/voiceAgents.helpers'

import OverrideEditor from './OverrideEditor'

// =============================================================================
// ToolsTab — lists the tools the current node calls. Each tool that resolves
// to a tool-node in the agent config is editable: the editable surface is the
// tool's description (the BE refuses every other field). Tools that don't
// resolve to a node (referenced-but-missing) stay display-only.
// =============================================================================

const EditableToolRow = ({ tool }) => {
  const dispatch = useDispatch()
  const agentId = useSelector((state) => state.voiceAgents.selectedAgentId)
  const versions = useSelector((state) => state.voiceAgents.versionsByToolName[tool.name] || [])
  const isSaving = useSelector((state) => Boolean(state.voiceAgents.loading.saveTool))
  const isReverting = useSelector((state) => Boolean(state.voiceAgents.loading.revertTool))
  const isResetting = useSelector((state) => Boolean(state.voiceAgents.loading.resetTool))

  useEffect(() => {
    if (agentId && tool.name && isEmpty(versions)) {
      dispatch(fetchToolVersions(agentId, tool.name))
    }
  }, [agentId, tool.name, versions.length, dispatch])

  const handleSave = (nextValue) => dispatch(saveTool(agentId, tool.name, nextValue))
  const handleRevert = (version) => dispatch(revertTool(agentId, tool.name, version))
  const handleReset = () => dispatch(resetTool(agentId, tool.name))

  return (
    <li className="voice-agents__tool-row" data-testid={`tool-row-${tool.name}`}>
      <OverrideEditor
        testIdPrefix={`tool-${tool.name}`}
        icon={<IconTool size={14} />}
        label={<code>{tool.name}</code>}
        displayName={tool.name}
        value={tool.description}
        editorRows={4}
        editorPlaceholder="Describe when the model should call this tool"
        versions={versions}
        hasOverride={Boolean(tool.hasOverride)}
        isSaving={isSaving}
        isReverting={isReverting}
        isResetting={isResetting}
        onSave={handleSave}
        onRevert={handleRevert}
        onReset={handleReset}
      />
    </li>
  )
}

// Referenced-but-unresolved tool: shares the card chrome + wrench avatar/name
// header so it reads as the same kind of row, with a muted note instead of an
// editable description (there is no node to edit).
const MissingToolRow = ({ tool }) => (
  <li className="voice-agents__tool-row" data-testid={`tool-row-${tool.name}`} data-missing="true">
    <div className="voice-agents__override-editor">
      <div className="voice-agents__override-editor-head">
        <div className="voice-agents__override-editor-title">
          <span className="voice-agents__override-editor-avatar">
            <IconTool size={14} />
          </span>
          <span className="voice-agents__override-editor-label">
            <code>{tool.name}</code>
          </span>
        </div>
      </div>
      <div className="voice-agents__override-editor-plain voice-agents__override-editor-plain--empty">
        Tool not found
      </div>
    </div>
  </li>
)

const ToolsTab = ({ node }) => {
  const agent = useSelector((state) => getSelectedAgent(state.voiceAgents))
  const toolNames = node?.config?.tools || []

  const tools = useMemo(() => {
    if (!agent || !Array.isArray(toolNames)) {
      return []
    }
    return toolNames.map((name) => {
      const matchingNode = agent.nodes?.find(
        (entry) => entry.type === 'tool' && (entry.config?.name === name || entry.id === name)
      )
      return {
        name,
        description: matchingNode?.config?.description || '',
        params: matchingNode?.config?.params || [],
        hasOverride: Boolean(matchingNode?.config?.hasOverride),
        resolved: Boolean(matchingNode),
      }
    })
  }, [agent, toolNames])

  if (isEmpty(tools)) {
    return (
      <div className="voice-agents__drawer-tab" data-testid="drawer-tab-tools">
        <p className="voice-agents__drawer-tab-hint">This node does not call any tools directly.</p>
      </div>
    )
  }

  return (
    <div className="voice-agents__drawer-tab" data-testid="drawer-tab-tools">
      <ul className="voice-agents__tool-list">
        {tools.map((tool) =>
          tool.resolved ? (
            <EditableToolRow key={tool.name} tool={tool} />
          ) : (
            <MissingToolRow key={tool.name} tool={tool} />
          )
        )}
      </ul>
    </div>
  )
}

export default ToolsTab
